import { AfterViewInit, Component, DestroyRef, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { filter, merge } from 'rxjs';
import { SidebarComponent } from '../../components/common/sidebar/sidebar.component';
import { UserSignoutComponent } from '../../components/common/user-signout/user-signout.component';
import { UserNotificationService } from '../../services/signalr/user-notification.service';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth/auth.service';
import { CrossTabAuthSyncService } from '../../services/auth/cross-tab-auth-sync.service';
import { SessionEndDiagnosticsService } from '../../services/auth/session-end-diagnostics.service';
import { ConversionProgressService } from '../../services/signalr/conversion-progress.service';
import { FilingSessionUploader } from '../../services/mbrs/filing-session-uploader.service';
import { CurrentSheetCacheService } from '../../services/mbrs/current-sheet-cache.service';
import { FilingFormPersistenceService } from '../../services/mbrs/filing-form-persistence.service';
import { SheetLoadTraceService } from '../../services/mbrs/sheet-load-trace.service';
import { AccessDeniedNoticeService } from '../../services/common/access-denied-notice.service';
import { buildConversionProgressToastSummary } from '../../utils/conversion-progress-toast.util';
import { PreviousUrlTrackerService } from '../../services/common/previous-url-tracker.service';
import { MessageService } from 'primeng/api';
import { InteractiveToastComponent } from '../../components/common/interactive-toast/interactive-toast.component';
import { CacheDebugBadgeComponent } from '../../components/common/cache-debug-badge/cache-debug-badge.component';
import {
  TOAST_DURATION_LONG_MS,
} from '../../utils/toast-durations.util';
// [AS-569 debug] uncomment alongside the commented ForceLogout console trace.
// import { environment } from '../../../environments/environment';

@Component({
  selector: 'main-layout',
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    UserSignoutComponent,
    Dialog,
    Button,
    InteractiveToastComponent,
    CacheDebugBadgeComponent,
  ],
  templateUrl: './main-layout.component.html',
  providers: [UserNotificationService, MessageService],
})
export class MainLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'MBRS';
  timeToLogout = signal<number>(10);
  isUserEditDialogVisible = signal<boolean>(false);
  isUserDeleteDialogVisible = signal<boolean>(false);
  userNotificationService = inject(UserNotificationService);
  authService = inject(AuthService);
  router = inject(Router);
  conversionProgressService = inject(ConversionProgressService);
  private readonly crossTabAuth = inject(CrossTabAuthSyncService);
  private readonly sessionEndDiagnostics = inject(SessionEndDiagnosticsService);
  messageService = inject(MessageService);
  private readonly filingSessionUploader = inject(FilingSessionUploader);
  private readonly filingPersistence = inject(FilingFormPersistenceService);
  private readonly loadTrace = inject(SheetLoadTraceService);
  private readonly accessDeniedNotice = inject(AccessDeniedNoticeService);
  // Eagerly instantiate so its router-event listener starts tracking
  // navigations from the very first /mbrs/* mount — otherwise the
  // form → listing return that triggers cache invalidation could fire
  // before the service is ever injected elsewhere.
  private readonly currentSheetCache = inject(CurrentSheetCacheService);
  // Eagerly instantiate for the identical reason — see its own docblock.
  // `FilingFormContainerComponent.redirectOnPermissionFailure` reads
  // `getPreviousUrl()` only once the user has ALREADY navigated to a
  // forbidden conversion; the tracker must already be listening by then.
  private readonly previousUrlTracker = inject(PreviousUrlTrackerService);
  private readonly destroyRef = inject(DestroyRef);
  updatedTime = signal<Date | null>(null);
  editLogoutCountdown = signal<number>(10);
  private editDialogInterval: ReturnType<typeof setInterval> | null = null;
  private editLogoutDeadlineMs = 0;
  private isEditLogoutInProgress = false;
  isCollapsed = signal<boolean>(false);
  isMobileSidebarOpen = signal<boolean>(false);
  private deleteDialogInterval: ReturnType<typeof setInterval> | null = null;

  toggleMobileSidebar() {
    this.isMobileSidebarOpen.set(!this.isMobileSidebarOpen());
  }

  closeMobileSidebar() {
    this.isMobileSidebarOpen.set(false);
  }

  private connectSignalR(): void {
    this.userNotificationService.connect().then(() => {
      // All four handlers go through UserNotificationService.on(...), which
      // re-enters Angular's NgZone. SignalR transport callbacks fire outside
      // the zone, so a raw getHubConnection().on(...) would schedule no change
      // detection — dialogs/redirects would only appear on the next click.
      this.userNotificationService.on('UserUpdateScheduled', (userTime) => {
        this.updatedTime.set(userTime as Date);
        this.isUserEditDialogVisible.set(true);
        this.startEditLogoutCountdown();
      });
      this.userNotificationService.on('UserUpdatedCompleted', (data) => {
        if (data) {
          this.forceLogoutFromEdit();
        }
      });
      this.userNotificationService.on('UserDeletedCompleted', (data) => {
        if (data) {
          this.calculateTimeToLogout();
        }
      });
      // Single-active-session-per-device, InstantPush mode: the backend
      // pushes ForceLogout only to the *displaced* device's connections when
      // the same user logs in elsewhere. We tear the UI down immediately with
      // the "signed in elsewhere" copy. Safe no-op when the backend runs in
      // the default OnNextApiCall mode — the event simply never fires, and the
      // sessionExpiryInterceptor handles the displacement on the next API call.
      this.userNotificationService.on('ForceLogout', () => {
        // Breadcrumb so a future incident shows a push DID precede the logout.
        // Only genuine displacement sends this push; a stale cookie never does,
        // so the absence of this line next to a 401 line is the "stale" tell.
        this.sessionEndDiagnostics.record({
          source: 'force-logout-push',
          action: 'force-logout',
          route: this.router.url,
          pushArrived: true,
        });
        // [AS-569 debug] confirms the zoned ForceLogout handler body ran —
        // uncomment (with the `environment` import) to re-enable. Dev only.
        // if (!environment.production) {
        //   console.log('[UserHub] ForceLogout handler running → calling forceLogout()', {
        //     timestamp: new Date().toISOString(),
        //   });
        // }
        this.authService.forceLogout('session-superseded');
      });
    });
  }

  private connectConversionProgressSignalR(): void {
    this.conversionProgressService.startConnection();

    // The global toast is phase-agnostic — Phase 1 and Phase 2 progress
    // events show the same "uploaded successfully, please review" message.
    // Both streams are merged so the single handler below covers either phase.
    merge(
      this.conversionProgressService.progress1st$,
      this.conversionProgressService.progress2nd$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        // Links to the listing, not the filing page: the conversion is most
        // likely still in progress here. See the builder's docblock.
        const summary = buildConversionProgressToastSummary(data.fsFileName);
        this.messageService.add({
          key: 'form',
          severity: 'custom-warning',
          summary,
          life: TOAST_DURATION_LONG_MS,
        });
      });
  }

  /**
   * Forwards final upload results (success / failure of the 3-step filing
   * session flow) from the root-provided uploader service to this layout's
   * MessageService, so the toast still fires when the user has navigated away
   * from the dashboard widget before the upload finished.
   *
   * DEVELOPER NOTE (toast-key collision): the SignalR listeners below in
   * connectConversionProgressSignalR() also push toasts with key 'form' on
   * conversion-progress events. A successful upload finishing alongside a
   * 1st/2nd conversion-progress ping will stack two 'form' toasts — accepted
   * trade-off. See filing-session-uploader.service.ts for the matching note.
   */
  private subscribeToFilingUploadResults(): void {
    this.filingSessionUploader.result$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.kind === 'success') {
          this.messageService.add({
            key: 'form',
            severity: 'custom-info',
            summary: `We are converting <b>${event.fsFileName}</b> and will notify you when it's ready`,
            life: TOAST_DURATION_LONG_MS,
          });
        } else {
          // `life`, never `sticky`. PrimeNG's `sticky` short-circuits the
          // dismiss timer outright — it does not merely lengthen it, and it
          // ignores any `life` set alongside it — so every uploader error
          // (not just the FS content codes) used to hang on screen until the
          // user clicked the close button. Kept at the same LONG duration as
          // the success branch above so both outcomes of one upload dismiss on
          // the same rhythm; the close button remains for early dismissal.
          this.messageService.add({
            key: 'form',
            severity: 'custom-error',
            summary: event.message,
            life: TOAST_DURATION_LONG_MS,
          });
        }
      });
  }

  /**
   * Developer Note (Single User Session Per Device): Cross-tab logout receiver (customer scope). When another customer tab runs
   * a voluntary logout, AuthService broadcasts it and this handler tears the
   * current tab down via forceLogout('logged-out') — which is idempotent,
   * navigates to /auth/login (no message shown — a forced logout is silent as
   * of PO 2026-06-04; see AuthService.forceLogout), and triggers this
   * component's ngOnDestroy to close the SignalR hubs. Mirrors the existing
   * `ForceLogout` SignalR behaviour exactly, just client-driven.
   *
   * Scope-filtered to 'customer' so a dev-portal logout never disturbs this
   * tab. forceLogout() does NOT re-broadcast, so there is no echo storm.
   */
  private subscribeToCrossTabLogout(): void {
    this.crossTabAuth.logout$
      .pipe(
        filter((message) => message.scope === 'customer'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.authService.forceLogout('logged-out');
      });
  }

  /**
   * Cross-SCOPE login teardown. The customer and dev areas are mutually
   * exclusive per browser: signing in to the developer portal on any tab must
   * sign this customer tab out (and the reverse is handled by DevLayout). We
   * listen for a 'dev' LOGIN broadcast and forceLogout — which clears customer
   * state and redirects to /auth/login with no message shown (a forced logout
   * is silent as of PO 2026-06-04; see AuthService.forceLogout).
   * forceLogout is idempotent and does NOT re-broadcast, so there is no echo
   * storm.
   *
   * `preserveDevSession: true` is critical: the dev JWT lives in a browser-wide
   * cookie that the dev tab JUST created, so this customer teardown must NOT
   * clear it (a plain forceLogout would, deleting the brand-new dev session).
   *
   * Scope is the OTHER scope here ('dev'), unlike subscribeToCrossTabLogout
   * above which filters its OWN scope ('customer'). A same-scope customer login
   * is deliberately ignored — this tab already holds that very session.
   */
  private subscribeToCrossScopeLogin(): void {
    this.crossTabAuth.login$
      .pipe(
        filter((message) => message.scope === 'dev'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.authService.forceLogout('scope-switched', { preserveDevSession: true, });
      });
  }

  ngOnInit(): void {
    this.connectSignalR();
    this.connectConversionProgressSignalR();
    this.subscribeToFilingUploadResults();
    this.subscribeToCrossTabLogout();
    this.subscribeToCrossScopeLogin();
    this.subscribeToPersistenceErrors();
    this.subscribeToFilingLoadFailures();
  }

  /**
   * Show a "you may not open this page" toast staged by a route guard.
   *
   * Runs from `ngAfterViewInit`, not `ngOnInit`, because a guard that refuses a
   * typed URL stages its line BEFORE this shell exists: the child `<p-toast>`
   * subscribes to `MessageService` in its own `ngOnInit`, which runs after this
   * component's, so a message added any earlier is lost without an error.
   * `ngAfterViewInit` is the first hook after every child has initialised. The
   * `staged$` subscription covers a guard that fires while the shell is already
   * mounted (an in-app click).
   */
  ngAfterViewInit(): void {
    this.showPendingPreNavigationNotice();
    this.showStagedAccessDeniedNotice();
    this.accessDeniedNotice.staged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showStagedAccessDeniedNotice());
  }

  /**
   * Show a filing-guard refusal (`conversionExistsGuard`) raised on a cold load,
   * before this shell existed — see
   * `SheetLoadTraceService.reportPreNavigationFailure`. Drained from the same
   * hook as the access-denied notice, for the same reason: the toast outlet
   * subscribes in its own `ngOnInit`, so anything added earlier is dropped. An
   * in-app click needs no equivalent here — the guard emits on `failures$`,
   * which this shell already listens to.
   */
  private showPendingPreNavigationNotice(): void {
    const notice = this.loadTrace.consumePendingPreNavigationNotice();
    if (notice) {
      this.messageService.add({ severity: 'error', summary: notice.summary, life: notice.life });
    }
  }

  private showStagedAccessDeniedNotice(): void {
    const summary = this.accessDeniedNotice.consume();
    if (summary) {
      this.messageService.add({ severity: 'error', summary, life: TOAST_DURATION_LONG_MS });
    }
  }

  /**
   * Surface filing-form persistence (IndexedDB) write failures as a toast.
   * FilingFormPersistenceService is `providedIn: 'root'` and cannot inject the
   * component-scoped MessageService, so it emits on writeErrors$ and this
   * always-mounted shell renders the toast.
   */
  /**
   * Surface filing-load failures (the `MBRS-…` diagnostic code) as a toast.
   *
   * Rendered HERE rather than by the filing container for two reasons, both
   * measured rather than assumed:
   *
   * 1. `SheetLoadTraceService` is `providedIn: 'root'` and so cannot inject the
   *    component-scoped `MessageService` — the same constraint that put
   *    `writeErrors$` above on this shell.
   * 2. **The container's own `<p-toast>` cannot show these for every class.**
   *    `FilingFormContainerComponent.handleContainerLoadFailure` navigates
   *    away for `PERM` (to wherever the user came from, via
   *    `router.navigateByUrl()`), which destroys the container and its toast
   *    outlet in the same tick the message is added. The silent `AUTH` class
   *    raises no toast here at all — it is staged for the login page, which
   *    `forceLogout` navigates to. Every other class stays on the filing
   *    page behind `FilingLoadFailurePanelComponent`. This layout is the
   *    parent of both `/mbrs/filing` and `/mbrs/dashboard`, so it is still
   *    mounted on the other side of a redirect.
   *
   * `PERM`'s redirect is deliberately always a plain in-app Angular
   * navigation (`router.navigate`/`router.navigateByUrl`), never
   * `Location.back()` — an earlier revision used `Location.back()` and found
   * it could restore the destination from the browser's back-forward cache
   * instead of navigating at all, during which no script runs and this
   * subscription's callback never fires. See
   * `FilingFormContainerComponent.redirectOnPermissionFailure` and
   * `PreviousUrlTrackerService` for why `router.navigateByUrl()` to a
   * TRACKED previous URL replaced it rather than adding machinery to cope
   * with that failure mode.
   */
  private subscribeToFilingLoadFailures(): void {
    this.loadTrace.failures$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ summary, life }) => {
        this.messageService.add({ severity: 'error', summary, life });
      });
  }

  private subscribeToPersistenceErrors(): void {
    this.filingPersistence.writeErrors$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ summary, detail }) => {
        this.messageService.add({ severity: 'error', summary, detail });
      });
  }

  ngOnDestroy(): void {
    this.conversionProgressService.stopConnection();
    // Close the UserHub connection too. This fires on every navigation away
    // from the authenticated area — voluntary logout, forced logout
    // (session superseded / expired), or user-deletion — so a stale hub
    // connection never lingers after the session is gone.
    void this.userNotificationService.disconnect();
    if (this.deleteDialogInterval) {
      clearInterval(this.deleteDialogInterval);
      this.deleteDialogInterval = null;
    }
    if (this.editDialogInterval) {
      clearInterval(this.editDialogInterval);
      this.editDialogInterval = null;
    }
  }

  /**
   * Client-guaranteed logout countdown for the "Important Account Update"
   * dialog (shown when an admin changes THIS user's ROLE — after the backend
   * gating, non-role changes never open this dialog). Uses a wall-clock
   * DEADLINE rather than a per-tick decrement so the logout still fires
   * promptly after the device wakes from sleep: a plain decrement pauses while
   * the machine sleeps and would resume counting, delaying the logout. This is
   * the primary (~10s) trigger; the backend `UserUpdatedCompleted` push and the
   * next-request 401 remain as fallbacks.
   */
  private startEditLogoutCountdown(): void {
    this.editLogoutDeadlineMs = Date.now() + 10_000;
    this.editLogoutCountdown.set(10);
    if (this.editDialogInterval) {
      clearInterval(this.editDialogInterval);
    }
    this.editDialogInterval = setInterval(() => {
      const remainingMs = this.editLogoutDeadlineMs - Date.now();
      if (remainingMs > 0) {
        this.editLogoutCountdown.set(Math.ceil(remainingMs / 1000));
      } else {
        this.forceLogoutFromEdit();
      }
    }, 1000);
  }

  /**
   * Single, idempotent logout path for the edit/role dialog. Every trigger —
   * the countdown expiring, the backend `UserUpdatedCompleted` push, and the
   * manual "Logout" button (closeUserEditDialog) — routes through here, so they
   * can never fire two competing logout() calls / navigations. Mirrors the
   * delete dialog's guarded teardown.
   */
  private forceLogoutFromEdit(): void {
    if (this.isEditLogoutInProgress) {
      return;
    }
    this.isEditLogoutInProgress = true;
    if (this.editDialogInterval) {
      clearInterval(this.editDialogInterval);
      this.editDialogInterval = null;
    }
    this.isUserEditDialogVisible.set(false);
    this.authService.logout().subscribe({
      next: () => this.router.navigate(['/auth/login']),
      error: () => this.router.navigate(['/auth/login']),
    });
  }

  closeUserEditDialog() {
    this.forceLogoutFromEdit();
  }

  closeUserDeleteDialog() {
    if (this.deleteDialogInterval) {
      clearInterval(this.deleteDialogInterval);
      this.deleteDialogInterval = null;
    }
    this.isUserDeleteDialogVisible.set(false);
    this.timeToLogout.set(10);
    // Call logout first to clear cookies on backend, then navigate
    this.authService.logout().subscribe({
      next: () => {
        this.authService.clearAuthState();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        // Even on error, clear state and navigate
        this.authService.clearAuthState();
        this.router.navigate(['/auth/login']);
      }
    });
  }

  calculateTimeToLogout() {
    this.isUserDeleteDialogVisible.set(true);
    this.deleteDialogInterval = setInterval(() => {
      if (this.timeToLogout() > 1) {
        this.timeToLogout.set(this.timeToLogout() - 1);
      } else {
        if (this.deleteDialogInterval) {
          clearInterval(this.deleteDialogInterval);
          this.deleteDialogInterval = null;
        }
        this.isUserDeleteDialogVisible.set(false);
        this.timeToLogout.set(10);
        // Call logout first to clear cookies on backend, then navigate
        this.authService.logout().subscribe({
          next: () => {
            this.authService.clearAuthState();
            this.router.navigate(['/auth/login']);
          },
          error: () => {
            // Even on error, clear state and navigate
            this.authService.clearAuthState();
            this.router.navigate(['/auth/login']);
          }
        });
      }
    }, 1000);
  }

  onSidebarWidthChange(isCollapsed: boolean) {
    this.isCollapsed.set(isCollapsed);
  }
}
